import React, { useState, useEffect, useRef } from 'react';
import SpeechRecognitionComponent from './SpeechRecognitionComponent'
import './ITVocabApp.css';
import _ from 'lodash';

// Định nghĩa kiểu dữ liệu
interface Category {
  id: string;
  name: string;
  file: string;
}

interface VocabExample {
  en: string;
  vi: string;
}

interface Vocabulary {
  id: string | number;
  word: string;
  pronunciation: string;
  type: string;
  meaning: string;
  examples: VocabExample;
  learned?: boolean;
}

interface LearnedVocabs {
  [categoryId: string]: Vocabulary[];
}

interface AllVocabsState {
  [categoryId: string]: Vocabulary[];
}

interface Quiz {
  question: string;
  options: Vocabulary[];
  correctAnswer: string;
  quizType: string;
  targetWord: Vocabulary;
}

interface QuizResult {
  isCorrect: boolean;
  correctAnswer: Vocabulary;
}

// Khai báo window.fs
declare global {
  interface Window {
    fs: {
      readFile(path: string, options?: { encoding?: string }): Promise<any>;
    }
  }
}

// Tạo giả lập fs nếu chưa tồn tại
if (typeof window !== 'undefined' && !window.fs) {
  window.fs = {
    async readFile(path: string, options?: { encoding?: string }) {
      try {
        const response = await fetch(`/data/${path}`);
        
        if (!response.ok) {
          throw new Error(`Failed to load file: ${path}`);
        }
        
        if (options?.encoding === 'utf8') {
          return await response.text();
        }
        
        return await response.arrayBuffer();
      } catch (error) {
        console.error("Error reading file:", error);
        throw error;
      }
    }
  };
}

// Main App Component
const ITVocabApp = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [allVocabs, setAllVocabs] = useState<AllVocabsState>({});
  const [dailyVocabs, setDailyVocabs] = useState<Vocabulary[]>([]);
  const [learnedVocabs, setLearnedVocabs] = useState<LearnedVocabs>({});
  const [activeTab, setActiveTab] = useState<string>("daily");
  const [currentQuiz, setCurrentQuiz] = useState<Quiz | null>(null);
  const [quizAnswer, setQuizAnswer] = useState<string>("");
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);

  // Initialize and load available categories
  useEffect(() => {
    const detectCategories = async () => {
      try {
        setIsLoading(true);
        
        // For demonstration, we'll use the IT file as a default
        // In a real scenario, we would scan for available files
        const categoriesData: Category[] = [
          { id: 'it', name: 'IT & Web Programming', file: 'it-web-programming-vocabulary.md' }
        ];
        
        // Add more example categories (in a real app, these would be detected from actual files)
        categoriesData.push({ id: 'business', name: 'Business & Finance', file: 'business-vocabulary.md' });
        categoriesData.push({ id: 'medical', name: 'Medical Terminology', file: 'medical-vocabulary.md' });
        
        setCategories(categoriesData);
        
        // Initialize empty learned vocabs object for each category
        const initialLearnedVocabs: LearnedVocabs = {};
        categoriesData.forEach(cat => {
          initialLearnedVocabs[cat.id] = [];
        });
        
        // Load saved learned vocabs from localStorage
        const savedLearned = localStorage.getItem('learnedVocabs');
        if (savedLearned) {
          setLearnedVocabs(JSON.parse(savedLearned));
        } else {
          setLearnedVocabs(initialLearnedVocabs);
        }
        
        // Set IT as the default selected category
        if (categoriesData.length > 0) {
          setSelectedCategory(categoriesData[0].id);
          await loadCategoryVocabulary(categoriesData[0]);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error detecting categories:', error);
        setIsLoading(false);
      }
    };
    
    detectCategories();
  }, []);
  
  // Load vocabulary for a specific category
  const loadCategoryVocabulary = async (category: Category) => {
    try {
      setIsLoading(true);
      
      // Check if we've already loaded this category
      if (allVocabs[category.id]) {
        // If already loaded, just generate daily words
        generateDailyVocabs(
          category.id, 
          allVocabs[category.id], 
          learnedVocabs[category.id] || []
        );
        setIsLoading(false);
        return;
      }
      
      // Load file content - only the IT file actually exists in our demo
      let fileContent: string;
      try {
        fileContent = await window.fs.readFile(category.file, { encoding: 'utf8' });
      } catch (error) {
        // For demo purposes, if file doesn't exist, create a placeholder
        if (category.id !== 'it') {
          const demoVocabs = generateDemoVocabulary(category.id, 30);
          setAllVocabs(prev => ({...prev, [category.id]: demoVocabs}));
          generateDailyVocabs(
            category.id, 
            demoVocabs, 
            learnedVocabs[category.id] || []
          );
          setIsLoading(false);
          return;
        } else {
          throw error; // Re-throw for the IT file which should exist
        }
      }
      
      // Parse the real file content
      const parsedVocabs = parseMarkdownVocabulary(fileContent);
      setAllVocabs(prev => ({...prev, [category.id]: parsedVocabs}));
      
      // Generate daily words for this category
      generateDailyVocabs(
        category.id, 
        parsedVocabs, 
        learnedVocabs[category.id] || []
      );
      
      setIsLoading(false);
    } catch (error) {
      console.error(`Error loading vocabulary for ${category.name}:`, error);
      setIsLoading(false);
    }
  };
  
  // Generate demo vocabulary for categories without actual files
  const generateDemoVocabulary = (categoryId: string, count: number): Vocabulary[] => {
    const demoVocabs: Vocabulary[] = [];
    const categoryPrefix = categoryId === 'business' 
      ? 'Business' 
      : categoryId === 'medical' 
        ? 'Medical' 
        : 'Tech';
    
    for (let i = 1; i <= count; i++) {
      demoVocabs.push({
        id: `${categoryId}-${i}`,
        word: `${categoryPrefix}Term${i}`,
        pronunciation: `/${categoryPrefix.toLowerCase()}tɜːm${i}/`,
        type: Math.random() > 0.5 ? 'n' : 'v',
        meaning: `Ví dụ nghĩa của từ ${categoryPrefix}Term${i}`,
        examples: {
          en: `This is an example sentence using ${categoryPrefix}Term${i}.`,
          vi: `Đây là câu ví dụ sử dụng ${categoryPrefix}Term${i}.`
        }
      });
    }
    
    return demoVocabs;
  };

  // Parse the markdown file to extract vocabulary items
  const parseMarkdownVocabulary = (markdownText: string): Vocabulary[] => {
    const lines = markdownText.split('\n');
    const vocabularyItems: Vocabulary[] = [];
    let currentItem: Vocabulary | null = null;

    for (const line of lines) {
      // Check if line contains a new vocabulary item (numbered list item)
      const vocabMatch = line.match(/^\d+\.\s+\*\*([^*]+)\*\*\s+\/([^/]+)\/\s+\(([^)]+)\):\s+(.+)/);
      
      if (vocabMatch) {
        if (currentItem) {
          vocabularyItems.push(currentItem);
        }
        
        currentItem = {
          id: vocabularyItems.length + 1,
          word: vocabMatch[1].trim(),
          pronunciation: vocabMatch[2].trim(),
          type: vocabMatch[3].trim(),
          meaning: vocabMatch[4].trim(),
          examples: { en: "", vi: "" },
        };
      } else if (currentItem && line.includes('*Example*:')) {
        currentItem.examples.en = line.replace('*Example*:', '').trim();
      } else if (currentItem && line.includes('*Ví dụ*:')) {
        currentItem.examples.vi = line.replace('*Ví dụ*:', '').trim();
      }
    }
    
    if (currentItem) {
      vocabularyItems.push(currentItem);
    }
    
    return vocabularyItems;
  };

  // Change selected category
  const handleCategoryChange = async (categoryId: string) => {
    setSelectedCategory(categoryId);
    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      await loadCategoryVocabulary(category);
    }
  };

  // Generate a set of daily vocabulary words
  const generateDailyVocabs = (categoryId: string, allWords: Vocabulary[], learnedWords: Vocabulary[]) => {
    // Use the current date as seed for consistent daily words
    const today = new Date().toDateString();
    
    // Get array of learned word IDs
    const learnedIds = learnedWords.map(word => word.id);
    
    // Filter out already learned words
    const unlearned = allWords.filter(word => !learnedIds.includes(word.id));
    
    // If we have less than 10 unlearned words, include some learned ones
    let pool = unlearned;
    if (unlearned.length < 10) {
      pool = [...unlearned, ..._.sampleSize(learnedWords, 10 - unlearned.length)];
    }
    
    // Use the date string to seed randomness for consistent daily selection
    const seededRandom = (str: string): number => {
      let seed = 0;
      for (let i = 0; i < str.length; i++) {
        seed = ((seed << 5) - seed) + str.charCodeAt(i);
        seed = seed & seed; // Convert to 32bit integer
      }
      return Math.abs(seed) / 2147483647;
    };
    
    const sortedWords = [...pool].sort((a, b) => {
      return seededRandom(`${today}-${categoryId}-${a.id}`) - seededRandom(`${today}-${categoryId}-${b.id}`);
    });
    
    setDailyVocabs(sortedWords.slice(0, 10));
  };

  // Function to speak text using speech synthesis
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9; // Slightly slower for clarity
      window.speechSynthesis.speak(utterance);
    } else {
      alert('Trình duyệt của bạn không hỗ trợ chức năng đọc văn bản.');
    }
  };

  // Mark vocabulary as learned
  const markAsLearned = (vocab: Vocabulary) => {
    const categoryLearned = learnedVocabs[selectedCategory] || [];
    const updatedCategoryLearned = [...categoryLearned];
    const existingIndex = updatedCategoryLearned.findIndex(v => v.id === vocab.id);
    
    if (existingIndex >= 0) {
      // Already in learned list, remove it
      updatedCategoryLearned.splice(existingIndex, 1);
    } else {
      // Add to learned list
      updatedCategoryLearned.push({...vocab, learned: true});
    }
    
    const updatedLearnedVocabs = {
      ...learnedVocabs,
      [selectedCategory]: updatedCategoryLearned
    };
    
    setLearnedVocabs(updatedLearnedVocabs);
    localStorage.setItem('learnedVocabs', JSON.stringify(updatedLearnedVocabs));
  };

  // Start a quiz with learned vocabulary
  const startQuiz = () => {
    setActiveTab("quiz");
    
    // We'll check the learned vocab count and generate a quiz in the UI
    // rather than showing an alert
    if ((learnedVocabs[selectedCategory] || []).length >= 4) {
      generateNewQuizQuestion();
    }
  };

  // Generate a new quiz question
  const generateNewQuizQuestion = () => {
    const currentLearned = learnedVocabs[selectedCategory] || [];
    if (currentLearned.length < 4) return;
    
    const quizTypes = [
      "meaning-to-word",
      "word-to-meaning",
      "example-to-word"
    ];
    
    const quizType = quizTypes[Math.floor(Math.random() * quizTypes.length)];
    const targetWord = _.sample(currentLearned) as Vocabulary;
    
    // Generate 3 incorrect options from all vocabulary
    const otherOptions = _.sampleSize(
      allVocabs[selectedCategory].filter(v => v.id !== targetWord.id), 
      3
    );
    
    const options = _.shuffle([targetWord, ...otherOptions]);
    
    let question = "";
    let correctAnswer = "";
    
    switch(quizType) {
      case "meaning-to-word":
        question = `Từ nào có nghĩa là: "${targetWord.meaning}"`;
        correctAnswer = targetWord.word;
        break;
      case "word-to-meaning":
        question = `"${targetWord.word}" có nghĩa là gì?`;
        correctAnswer = targetWord.meaning;
        break;
      case "example-to-word":
        question = `Ví dụ này liên quan đến từ vựng nào? "${targetWord.examples.en}"`;
        correctAnswer = targetWord.word;
        break;
      default:
        question = `Từ nào có nghĩa là: "${targetWord.meaning}"`;
        correctAnswer = targetWord.word;
    }
    
    setCurrentQuiz({
      question,
      options,
      correctAnswer,
      quizType,
      targetWord
    });
    
    setQuizAnswer("");
    setQuizResult(null);
  };

  // Check quiz answer
  const checkAnswer = () => {
    if (!quizAnswer || !currentQuiz) return;
    
    // Always compare the selected answer ID with the target word ID
    const isCorrect = quizAnswer === currentQuiz.targetWord.id.toString();
    
    setQuizResult({
      isCorrect,
      correctAnswer: currentQuiz.targetWord
    });
  };

  // Handle next question in quiz
  const nextQuestion = () => {
    generateNewQuizQuestion();
  };

  // Get count of learned words for current category
  const getCurrentLearnedCount = (): number => {
    return (learnedVocabs[selectedCategory] || []).length;
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-animation">
          <div className="loading-header"></div>
          <div className="loading-subtitle"></div>
          <div className="loading-cards">
            <div className="loading-card"></div>
            <div className="loading-card"></div>
            <div className="loading-card"></div>
          </div>
        </div>
      </div>
    );
  }

  // Render vocabulary card
  const VocabCard = ({ vocab, isLearned, onMarkLearned }: { vocab: Vocabulary, isLearned: boolean, onMarkLearned: (vocab: Vocabulary) => void }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [showPronunciation, setShowPronunciation] = useState(false);
    const [pronunciationScore, setPronunciationScore] = useState<number | null>(null);

     // Xử lý kết quả phát âm
    const handlePronunciationResult = (score: number, transcript: string) => {
      setPronunciationScore(score);
    };
    
    return (
      <div className={`vocab-card ${isLearned ? 'learned' : ''}`}>
        <div className="vocab-header">
          <div className="vocab-word-container">
            <h3 className="vocab-word">{vocab.word}</h3>
            <button 
              onClick={() => speakText(vocab.word)}
              className="sound-button"
              title="Nghe phát âm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
              </svg>
            </button>
          </div>
          <div className="vocab-actions">
            <button
              onClick={() => setShowPronunciation(!showPronunciation)}
              className="practice-button"
              title="Luyện phát âm"
            >
              {showPronunciation ? 'Ẩn luyện phát âm' : 'Luyện phát âm'}
            </button>
            <button
              onClick={() => onMarkLearned(vocab)}
              className={`mark-button ${isLearned ? 'learned' : 'not-learned'}`}
            >
              {isLearned ? 'Đã học' : 'Đánh dấu đã học'}
            </button>
          </div>
        </div>
        
        <div>
          <div className="vocab-pronunciation">
            /{vocab.pronunciation}/ 
            <span className="vocab-type">({vocab.type})</span>
          </div>
          
          <p className="vocab-meaning">Nghĩa: {vocab.meaning}</p>
          
          {showPronunciation && (
            <SpeechRecognitionComponent 
              targetWord={vocab.word}
              onResultCallback={handlePronunciationResult}
            />
          )}
          
          <div className="text-center">
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="details-button"
            >
              {showDetails ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  Ẩn chi tiết
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Xem chi tiết
                </>
              )}
            </button>
          </div>
          
          {showDetails && (
            <div className="vocab-details">
              <div className="mb-3">
                <div className="example-title">Ví dụ:</div>
                <p className="example-text example-en">{vocab.examples.en}</p>
              </div>
              <div>
                <div className="example-title">Ví dụ (Tiếng Việt):</div>
                <p className="example-text">{vocab.examples.vi}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title">Học Từ Vựng Chuyên Ngành</h1>
          <p className="app-subtitle">Học và kiểm tra kiến thức của bạn với từ vựng chuyên ngành</p>
        
          {/* Category Selection */}
          <div className="category-selection">
            <label className="category-label">Chọn lĩnh vực:</label>
            <div className="category-buttons">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="tab-navigation">
            <button 
              onClick={() => setActiveTab("daily")}
              className={`tab-button ${activeTab === "daily" ? 'active' : ''}`}
            >
              Từ vựng hàng ngày
            </button>
            <button 
              onClick={() => setActiveTab("learned")}
              className={`tab-button ${activeTab === "learned" ? 'active' : ''}`}
            >
              Đã học ({getCurrentLearnedCount()})
            </button>
            <button 
              onClick={startQuiz}
              className={`tab-button ${activeTab === "quiz" ? 'active' : ''}`}
            >
              Kiểm tra
            </button>
          </div>
        </div>
      </header>
      
      <main className="main-content">
        {activeTab === "daily" && (
          <div>
            <h2 className="section-title">
              <span className="section-title-accent">10 từ vựng hôm nay</span> - {categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <div>
              {dailyVocabs.map(vocab => (
                <VocabCard 
                  key={String(vocab.id)}
                  vocab={vocab}
                  isLearned={(learnedVocabs[selectedCategory] || []).some(v => v.id === vocab.id)}
                  onMarkLearned={markAsLearned}
                />
              ))}
            </div>
          </div>
        )}
        
        {activeTab === "learned" && (
          <div>
            <h2 className="section-title">
              <span className="section-title-accent">Từ vựng đã học</span> - {categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            {getCurrentLearnedCount() === 0 ? (
              <div className="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" className="empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="empty-text">Bạn chưa đánh dấu từ vựng nào là đã học trong lĩnh vực này.</p>
                <button 
                  onClick={() => setActiveTab("daily")}
                  className="primary-button"
                >
                  Học từ vựng ngay
                </button>
              </div>
            ) : (
              <div>
                {(learnedVocabs[selectedCategory] || []).map(vocab => (
                  <VocabCard 
                    key={String(vocab.id)}
                    vocab={vocab}
                    isLearned={true}
                    onMarkLearned={markAsLearned}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {activeTab === "quiz" && (
          <div className="quiz-container">
            <h2 className="quiz-title">
              <svg xmlns="http://www.w3.org/2000/svg" className="quiz-title-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Kiểm tra từ vựng - {categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            
            {!currentQuiz && getCurrentLearnedCount() >= 4 ? (
              <div className="quiz-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="quiz-start-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <p className="quiz-start-text">Sẵn sàng kiểm tra kiến thức của bạn?</p>
                <button
                  onClick={generateNewQuizQuestion}
                  className="quiz-start-button"
                >
                  Bắt đầu kiểm tra
                </button>
              </div>
            ) : !currentQuiz ? (
              <div className="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" className="empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="empty-text">Bạn cần học ít nhất 4 từ vựng trước khi làm bài kiểm tra.</p>
                <button 
                  onClick={() => setActiveTab("daily")}
                  className="primary-button"
                >
                  Học từ vựng ngay
                </button>
              </div>
            ) : (
              <>
                <p className="quiz-question">{currentQuiz.question}</p>
                
                <div className="quiz-options">
                  {currentQuiz.options.map(option => (
                    <label 
                      key={String(option.id)}
                      className={`quiz-option ${quizAnswer === option.id.toString() ? 'selected' : ''} 
                        ${quizResult && quizResult.correctAnswer.id === option.id ? 'correct' : ''} 
                        ${quizResult && quizAnswer === option.id.toString() && !quizResult.isCorrect ? 'incorrect' : ''}`}
                    >
                      <div className="quiz-option-content">
                        <input
                          type="radio"
                          name="quiz"
                          value={String(option.id)}
                          checked={quizAnswer === option.id.toString()}
                          onChange={(e) => setQuizAnswer(e.target.value)}
                          className="quiz-option-radio"
                          disabled={quizResult !== null}
                        />
                        {currentQuiz.quizType === "word-to-meaning" ? (
                          <span className="quiz-option-text">{option.meaning}</span>
                        ) : (
                          <div className="quiz-option-word">
                            <span>{option.word}</span>
                            {!quizResult && (
                              <button 
                                onClick={(e) => {
                                  e.preventDefault();
                                  speakText(option.word);
                                }}
                                className="sound-button"
                                title="Nghe phát âm"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                                </svg>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                
                {quizResult ? (
                  <div>
                    <div className={`quiz-result ${quizResult.isCorrect ? 'correct' : 'incorrect'}`}>
                      {quizResult.isCorrect ? (
                        <div className="quiz-result-header">
                          <svg xmlns="http://www.w3.org/2000/svg" className="quiz-result-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="quiz-result-text">Chính xác! Bạn đã trả lời đúng.</p>
                        </div>
                      ) : (
                        <div>
                          <div className="quiz-result-header">
                            <svg xmlns="http://www.w3.org/2000/svg" className="quiz-result-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <p className="quiz-result-text">Chưa đúng!</p>
                          </div>
                          <div className="quiz-result-answer">
                            <p>Đáp án đúng: <strong>{currentQuiz.targetWord.word}</strong> - {currentQuiz.targetWord.meaning}</p>
                            <button 
                              onClick={() => speakText(currentQuiz.targetWord.word)}
                              className="sound-button"
                              title="Nghe phát âm"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={nextQuestion}
                      className="quiz-next-button"
                    >
                      Câu hỏi tiếp theo
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={checkAnswer}
                    disabled={!quizAnswer}
                    className="quiz-check-button"
                  >
                    Kiểm tra đáp án
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </main>
      
      <footer className="app-footer">
        <p>© {new Date().getFullYear()} - Ứng dụng học từ vựng chuyên ngành</p>
      </footer>
    </div>
  );
};

export default ITVocabApp;